import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Chip,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Collapse,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Email,
  WhatsApp,
  Message,
  Search,
  FilterList,
  Refresh,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Error,
  Schedule,
  Send,
  Visibility,
  Reply,
} from '@mui/icons-material';
import { CommunicationRecord } from '@/types';
import { useCommunicationHistory } from '@/hooks/api/useGuests';
import { LoadingSpinner } from './LoadingSpinner';
import { ErrorMessage } from './ErrorMessage';

interface CommunicationHistoryPanelProps {
  bookingId: string;
  guestId?: string;
  title?: string;
  showFilters?: boolean;
  showActions?: boolean;
  maxHeight?: number;
  onSendMessage?: (guestId?: string) => void;
  onReply?: (originalMessage: CommunicationRecord) => void;
}

interface MessageDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  message: CommunicationRecord | null;
  onReply?: (message: CommunicationRecord) => void;
}

const MessageDetailsDialog: React.FC<MessageDetailsDialogProps> = ({
  open,
  onClose,
  message,
  onReply,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!message) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'sent':
        return 'info';
      case 'failed':
      case 'bounced':
        return 'error';
      default:
        return 'default';
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return <Email />;
      case 'whatsapp':
        return <WhatsApp />;
      default:
        return <Message />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {getChannelIcon(message.type)}
          <Box>
            <Typography variant="h6">
              {message.subject || `${message.type.charAt(0).toUpperCase() + message.type.slice(1)} Message`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {message.recipient}
            </Typography>
          </Box>
          <Chip
            label={message.status}
            color={getStatusColor(message.status)}
            size="small"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Message Details */}
          <Paper sx={{ p: 2 }} variant="outlined">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Sent At
                </Typography>
                <Typography variant="body1">
                  {new Date(message.sentAt).toLocaleString()}
                </Typography>
              </Grid>
              {message.deliveredAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Delivered At
                  </Typography>
                  <Typography variant="body1">
                    {new Date(message.deliveredAt).toLocaleString()}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Channel
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getChannelIcon(message.type)}
                  <Typography variant="body1">
                    {message.type.charAt(0).toUpperCase() + message.type.slice(1)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={message.status}
                  color={getStatusColor(message.status)}
                  size="small"
                />
              </Grid>
              {message.hookId && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Source
                  </Typography>
                  <Typography variant="body1">
                    Automated via Communication Hook
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Message Content */}
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="subtitle1" gutterBottom>
              Message Content
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {message.message}
            </Typography>
          </Paper>

          {/* Status Information */}
          {(message.status === 'failed' || message.status === 'bounced') && (
            <Alert severity="error">
              <Typography variant="body2">
                This message failed to deliver. You may want to try a different communication method or verify the recipient's contact information.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {onReply && message.status === 'delivered' && (
          <Button
            variant="contained"
            startIcon={<Reply />}
            onClick={() => onReply(message)}
          >
            Reply
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const CommunicationHistoryPanel: React.FC<CommunicationHistoryPanelProps> = ({
  bookingId,
  guestId,
  title = "Communication History",
  showFilters = true,
  showActions = true,
  maxHeight = 400,
  onSendMessage,
  onReply,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChannel, setFilterChannel] = useState<'all' | 'email' | 'whatsapp'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'sent' | 'delivered' | 'failed'>('all');
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [selectedMessage, setSelectedMessage] = useState<CommunicationRecord | null>(null);
  const [messageDetailsOpen, setMessageDetailsOpen] = useState(false);

  // API hooks
  const { 
    data: communicationHistory, 
    isLoading, 
    error,
    refetch 
  } = useCommunicationHistory(bookingId);

  // Filter communications
  const communicationData = Array.isArray(communicationHistory) ? communicationHistory : [];
  const filteredCommunications = communicationData.filter((comm: any) => {
    const matchesSearch = !searchTerm || 
      comm.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (comm.subject && comm.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
      comm.recipient.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesChannel = filterChannel === 'all' || comm.type === filterChannel;
    const matchesStatus = filterStatus === 'all' || comm.status === filterStatus;
    const matchesGuest = !guestId || comm.recipient.includes(guestId); // Simple guest filtering
    
    return matchesSearch && matchesChannel && matchesStatus && matchesGuest;
  });

  const handleToggleExpand = (messageId: string) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(messageId)) {
      newExpanded.delete(messageId);
    } else {
      newExpanded.add(messageId);
    }
    setExpandedMessages(newExpanded);
  };

  const handleViewDetails = (message: CommunicationRecord) => {
    setSelectedMessage(message);
    setMessageDetailsOpen(true);
  };

  const handleReply = (message: CommunicationRecord) => {
    setMessageDetailsOpen(false);
    onReply?.(message);
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return <Email fontSize="small" />;
      case 'whatsapp':
        return <WhatsApp fontSize="small" />;
      default:
        return <Message fontSize="small" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'sent':
        return 'info';
      case 'failed':
      case 'bounced':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle fontSize="small" />;
      case 'failed':
      case 'bounced':
        return <Error fontSize="small" />;
      default:
        return <Schedule fontSize="small" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <LoadingSpinner />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <ErrorMessage message="Failed to load communication history" />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {showActions && onSendMessage && (
            <Button
              variant="outlined"
              startIcon={<Send />}
              onClick={() => onSendMessage(guestId)}
              size="small"
            >
              Send Message
            </Button>
          )}
          <IconButton onClick={() => refetch()} size="small">
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Channel</InputLabel>
            <Select
              value={filterChannel}
              label="Channel"
              onChange={(e) => setFilterChannel(e.target.value as any)}
            >
              <MenuItem value="all">All Channels</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="whatsapp">WhatsApp</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value as any)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="delivered">Delivered</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Communications List */}
      <Box sx={{ maxHeight, overflow: 'auto' }}>
        {filteredCommunications.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || filterChannel !== 'all' || filterStatus !== 'all'
                ? 'No communications match your filters'
                : 'No communication history'
              }
            </Typography>
          </Box>
        ) : (
          <List>
            {filteredCommunications.map((comm: any, index: number) => {
              const isExpanded = expandedMessages.has(comm.recordId);
              const isLongMessage = comm.message.length > 150;
              
              return (
                <React.Fragment key={comm.recordId}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      {getChannelIcon(comm.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle2">
                            {comm.subject || `${comm.type.charAt(0).toUpperCase() + comm.type.slice(1)} Message`}
                          </Typography>
                          <Chip
                            label={comm.status}
                            color={getStatusColor(comm.status)}
                            size="small"
                            icon={getStatusIcon(comm.status)}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            To: {comm.recipient} • {formatDate(comm.sentAt)}
                            {comm.deliveredAt && ` • Delivered: ${formatDate(comm.deliveredAt)}`}
                          </Typography>
                          {comm.hookId && (
                            <Typography variant="caption" color="text.secondary">
                              Sent via communication hook
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {isExpanded || !isLongMessage
                              ? comm.message
                              : `${comm.message.substring(0, 150)}...`
                            }
                          </Typography>
                          {isLongMessage && (
                            <Button
                              size="small"
                              onClick={() => handleToggleExpand(comm.recordId)}
                              sx={{ mt: 0.5 }}
                            >
                              {isExpanded ? 'Show Less' : 'Show More'}
                            </Button>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(comm)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {onReply && comm.status === 'delivered' && (
                          <Tooltip title="Reply">
                            <IconButton
                              size="small"
                              onClick={() => onReply(comm)}
                            >
                              <Reply fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < filteredCommunications.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>

      {/* Message Details Dialog */}
      <MessageDetailsDialog
        open={messageDetailsOpen}
        onClose={() => setMessageDetailsOpen(false)}
        message={selectedMessage}
        onReply={handleReply}
      />
    </Paper>
  );
};

export default CommunicationHistoryPanel;